import { useState } from 'react'
import { Rnd } from 'react-rnd'
import type { CustomerCard as CustomerCardType } from '@/types/dashboard'
import { useCustomerTrackerStore } from '@/stores/customer-tracker-store'
import { X, Clock, GripVertical, Copy } from 'lucide-react'
import { CustomerTimeline } from './CustomerTimeline'

interface CustomerCardProps {
  customer: CustomerCardType
  drawMode?: boolean
}

export function CustomerCard({ customer, drawMode }: CustomerCardProps) {
  const [showTimeline, setShowTimeline] = useState(false)
  const { customers, removeCustomer, updatePosition, updateSize, duplicateCustomer } = useCustomerTrackerStore()

  // Get the actual customer data (follow linkedTo if this is a copy)
  const actualCustomer = customer.linkedTo
    ? customers.find((c) => c.id === customer.linkedTo) || customer
    : customer

  // Filter by actual date, not just the type field
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Start of today

  const futureFollowUps = actualCustomer.timeline?.filter((entry) => {
    const entryDate = new Date(entry.date)
    entryDate.setHours(0, 0, 0, 0)
    return entryDate >= today
  }) || []

  const pastEntries = actualCustomer.timeline?.filter((entry) => {
    const entryDate = new Date(entry.date)
    entryDate.setHours(0, 0, 0, 0)
    return entryDate < today
  }) || []

  // Get last past interaction date
  const lastInteraction = pastEntries.length > 0
    ? pastEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
    : null

  // Get first future date
  const nextFollowUp = futureFollowUps.length > 0
    ? futureFollowUps.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0]
    : null

  // Format date as MM/DD
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  // Fixed width for all cards, height is auto based on content
  const cardWidth = 300

  // Standard gray color for all cards
  const textColor = '#d1d5db'

  return (
    <>
      <Rnd
        size={{ width: cardWidth, height: 'auto' }}
        position={{ x: customer.position.x, y: customer.position.y }}
        onDragStop={(_e, d) => {
          updatePosition(customer.id, { x: d.x, y: d.y })
        }}
        onResizeStop={(_e, _direction, ref, _delta, position) => {
          updateSize(customer.id, {
            width: ref.offsetWidth,
          })
          updatePosition(customer.id, position)
        }}
        minWidth={120}
        bounds="parent"
        className="select-none group"
        dragHandleClassName="drag-handle"
        disableDragging={false}
        enableResizing={{
          right: true,
          left: true,
          top: false,
          bottom: false,
          topRight: false,
          bottomRight: false,
          bottomLeft: false,
          topLeft: false,
        }}
        style={{ pointerEvents: drawMode ? 'none' : 'auto' }}
      >
        <div
          className="w-full flex flex-col gap-1 py-2 px-1 cursor-pointer hover:bg-white/5 rounded transition-colors"
          onClick={() => setShowTimeline(true)}
        >
          <div className="flex items-start gap-1">
            {/* Drag handle */}
            <button
              className="drag-handle cursor-move flex-shrink-0 opacity-40 hover:opacity-100 transition-opacity p-0.5"
              style={{ color: textColor }}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="w-3 h-3" />
            </button>

            {/* Customer name */}
            <span className="text-xs font-medium flex-1 break-words leading-tight" style={{ color: textColor }}>
              {customer.name}
            </span>

            {/* Follow-up indicator */}
            {futureFollowUps.length > 0 && (
              <Clock className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: textColor }} />
            )}

            {/* Hover-only buttons */}
            <div className="flex-shrink-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Copy button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  duplicateCustomer(customer.id)
                }}
                className="p-0.5 text-white/40 hover:text-white/80 transition-colors"
                aria-label="Copy customer"
                title="Copy customer"
              >
                <Copy className="w-3 h-3" />
              </button>

              {/* Delete button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  removeCustomer(customer.id)
                }}
                className="p-0.5 text-white/40 hover:text-white/80 transition-colors"
                aria-label="Remove customer"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Dates and notes */}
          {(lastInteraction || nextFollowUp) && (
            <div className="flex flex-col gap-1 text-[10px] pl-5">
              {lastInteraction && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-green-400/60 font-medium">Last: {formatDate(lastInteraction.date)}</span>
                  {lastInteraction.note && (
                    <span className="text-green-300/50 text-[9px] break-words leading-snug">{lastInteraction.note}</span>
                  )}
                </div>
              )}
              {nextFollowUp && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-blue-400/60 font-medium">Next: {formatDate(nextFollowUp.date)}</span>
                  {nextFollowUp.note && (
                    <span className="text-blue-300/50 text-[9px] break-words leading-snug">{nextFollowUp.note}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Rnd>

      {showTimeline && <CustomerTimeline customer={actualCustomer} onClose={() => setShowTimeline(false)} />}
    </>
  )
}
